// Pieces the three row sets share: readiness, capacity, the objective widget.
import React, { useState } from 'react';
import { View } from 'react-native';
import { effectiveAims, type OracleSpineNode } from '../../olmps/oracle-spine-data';
import { SECTOR_DEITY_MAP } from '../../olmps/question-banks';
import { deityOf, goalTextOf } from '../../olmps/atlas-data';
import type { Sector } from '../../domain/olmps';
import { minOf, eventsOn, addDaysKey, cap, dateKeyOf, type DayEvent } from './time';
import { Btn, Field, Quiet, Txt, useK } from './ui';
import type { OrbData, OrbItem, RowCtx } from './types';

export interface Slot {
  ready: boolean;
  why?: string;
}

/** Time-slot readiness: has a time, and doesn't clash with a KRNOS event. */
export function slotState(item: OrbItem, events: DayEvent[]): Slot {
  if (item.completed) return { ready: true };
  if (!item.hasTime || !item.start) return { ready: false, why: 'no time slot' };
  const s = item.start.getHours() * 60 + item.start.getMinutes();
  const e = s + item.minutes;
  const hit = events.find((ev) => ev.startMin < e && ev.endMin > s);
  return hit ? { ready: false, why: `clashes with ${hit.title}` } : { ready: true };
}

export const eventsFor = (c: RowCtx, dayKey: string) => eventsOn(c.today.calendarEvents, dayKey);

export const aimsOf = (node: OracleSpineNode | null) => (node ? effectiveAims(node) : []);
/** The Goal stage (ATLAS monthly outcome). The sector deity is NOT the Goal — see deityLabel. */
export function goalName(_sector: Sector | null, node: OracleSpineNode | null): string | null {
  return goalTextOf(node) || null;
}
/** The sector deity the Endeavour trains (legacy spine key `goal`). */
export function deityLabel(sector: Sector | null, node: OracleSpineNode | null): string | null {
  const id = deityOf(node);
  if (!id) return null;
  const d = sector ? SECTOR_DEITY_MAP[sector]?.find((x) => x.id === id) : undefined;
  return d ? cap(d.name) : cap(id);
}

/** Where "Move" sends an item: the next day (today if it is overdue). */
export function moveTarget(item: OrbItem, todayKey: string): string {
  return item.date < todayKey ? todayKey : addDaysKey(item.date, 1);
}

/** Free minutes from now to the end of the counted week vs minutes the open items need. */
export function capacity(c: RowCtx, open: OrbItem[]): { freeH: number; needH: number } {
  const { game, weekDays } = c.data;
  const start0 = minOf(game.morningSessionTime);
  const end = minOf(game.eveningSessionTime);
  const nowMin = c.now.getHours() * 60 + c.now.getMinutes();
  const todayKey = dateKeyOf(c.now);
  let free = 0;
  for (const d of weekDays) {
    if (d.key < todayKey) continue;
    const from = d.key === todayKey ? Math.max(start0, nowMin) : start0;
    if (end <= from) continue;
    let booked = 0;
    for (const ev of eventsFor(c, d.key)) booked += Math.max(0, Math.min(end, ev.endMin) - Math.max(from, ev.startMin));
    free += Math.max(0, end - from - booked);
  }
  const need = open.reduce((n, i) => n + i.minutes, 0);
  return { freeH: Math.round((free / 60) * 10) / 10, needH: Math.round((need / 60) * 10) / 10 };
}

export const joinLine = (parts: (string | null | undefined | false)[]) => parts.filter(Boolean).join(' · ');

/** What an answer says when the tools hold nothing for it — never a guess, never a fail. */
export const NOT_SET = 'Not established';

/** "Aim X → Goal Y → Endeavour Z" from whichever links exist (null when none do). */
export function chainOf(parts: [string, string | null | undefined][]): string | null {
  const s = parts.filter(([, v]) => !!v).map(([k, v]) => (k ? `${k} ${v}` : v!));
  return s.length ? s.join(' → ') : null;
}

/** O row widget at Aim and Today: the pre-filled objective, what it serves, and Accept / Correct / Change. */
export function ObjectiveWidget({
  text,
  serves,
  state,
  empty,
  onAccept,
  onCorrect,
  onChange,
}: {
  text: string | null;
  serves: string | null;
  state?: 'accepted' | 'corrected';
  empty: string;
  onAccept: () => void;
  onCorrect: (text: string) => void;
  onChange: () => void;
}) {
  const k = useK();
  const [editing, setEditing] = useState(false);
  const [v, setV] = useState(text ?? '');
  const save = () => {
    if (v.trim()) onCorrect(v.trim());
    setEditing(false);
  };
  return (
    <View style={{ alignSelf: 'stretch', gap: 6 * k, alignItems: 'center' }}>
      {editing ? (
        <View style={{ alignSelf: 'stretch', flexDirection: 'row', gap: 5 * k, alignItems: 'center' }}>
          <View style={{ flex: 1 }}>
            <Field value={v} onChangeText={setV} onSubmit={save} placeholder="Objective" />
          </View>
          <Btn label="Save" tone="gold" disabled={!v.trim()} onPress={save} />
        </View>
      ) : text ? (
        <Txt style={{ fontSize: 15 * k, lineHeight: 19 * k, fontWeight: '800', textAlign: 'center' }} lines={3}>
          {text}
        </Txt>
      ) : (
        <Quiet>{empty}</Quiet>
      )}
      {serves ? (
        <Txt soft style={{ textAlign: 'center' }} lines={2}>
          serves {serves}
        </Txt>
      ) : null}
      {state && !editing ? (
        <Txt faint style={{ textAlign: 'center' }}>
          {state === 'accepted' ? '✓ Accepted' : '✎ Corrected'}
        </Txt>
      ) : null}
      <View style={{ flexDirection: 'row', gap: 6 * k }}>
        <Btn label="Accept" tone={state === 'accepted' ? 'ok' : 'gold'} disabled={!text} onPress={onAccept} />
        <Btn
          label="Correct"
          onPress={() => {
            setV(text ?? '');
            setEditing((e) => !e);
          }}
        />
        <Btn label="Change" onPress={onChange} />
      </View>
    </View>
  );
}

export const openOnly = (items: OrbItem[]) => items.filter((i) => !i.completed);
export type { OrbData };
